import React, { useState, useEffect, useCallback } from 'react';
import { useZoneRecords } from '../hooks/useZones';
import { zonesAPI, recordsAPI } from '../services/api';
import RecordModal from './RecordModal';
import SubdomainTree from './SubdomainTree';
import BulkActions from './BulkActions';
import RecordTemplates from './RecordTemplates';

const TYPE_COLORS = {
    A: { bg: '#dbeafe', color: '#1e40af' },
    AAAA: { bg: '#ede9fe', color: '#5b21b6' },
    CNAME: { bg: '#fff7ed', color: '#c2410c' },
    MX: { bg: '#d1fae5', color: '#065f46' },
    TXT: { bg: '#fef3c7', color: '#92400e' },
    NS: { bg: '#f3f4f6', color: '#374151' },
    SRV: { bg: '#fce7f3', color: '#9d174d' },
    CAA: { bg: '#ccfbf1', color: '#134e4a' },
};

const ZoneRecords = ({ zone, onBack }) => {
    const { records, loading: recordsLoading, refetch: refetchRecords } = useZoneRecords(zone?.id);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [fixingCNAMEs, setFixingCNAMEs] = useState(false);
    const [message, setMessage] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [selectedRecords, setSelectedRecords] = useState(new Set());
    const [propagationStatus, setPropagationStatus] = useState({});
    const [showTemplates, setShowTemplates] = useState(false);
    const [notes, setNotes] = useState({});

    // Load notes
    useEffect(() => {
        if (zone?.id) {
            zonesAPI.getNotes(zone.id).then(({ data }) => {
                if (data.success) {
                    const map = {};
                    data.notes.forEach(n => { map[n.record_id] = n; });
                    setNotes(map);
                }
            }).catch(() => {});
        }
    }, [zone?.id]);

    const filteredRecords = records.filter(record =>
        record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleCreateRecord = () => { setEditingRecord(null); setShowModal(true); };
    const handleEditRecord = (record) => { setEditingRecord(record); setShowModal(true); };

    const handleDeleteRecord = async (record) => {
        if (!window.confirm(`Tem certeza que deseja deletar o registro ${record.name}?`)) return;
        try {
            await zonesAPI.deleteRecord(zone.id, record.id);
            setMessage({ type: 'success', text: 'Registro deletado com sucesso!' });
            refetchRecords();
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao deletar registro: ${error.message}` });
        }
    };

    const handleSaveRecord = async (recordData) => {
        try {
            if (editingRecord) {
                await zonesAPI.updateRecord(zone.id, editingRecord.id, recordData);
                setMessage({ type: 'success', text: 'Registro atualizado com sucesso!' });
            } else {
                await zonesAPI.createRecord(zone.id, recordData);
                setMessage({ type: 'success', text: 'Registro criado com sucesso!' });
            }
            setShowModal(false);
            refetchRecords();
        } catch (error) {
            setMessage({ type: 'error', text: `Erro: ${error.response?.data?.errors?.[0]?.message || error.message}` });
        }
    };

    const handleFixCNAMEs = async () => {
        if (!window.confirm('Deseja corrigir todos os CNAMEs relativos neste dominio?')) return;
        try {
            setFixingCNAMEs(true);
            const response = await zonesAPI.fixCNAMEs(zone.id);
            setMessage({ type: 'success', text: `${response.data.fixed} CNAME(s) corrigido(s) com sucesso!` });
            refetchRecords();
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao corrigir CNAMEs: ${error.message}` });
        } finally {
            setFixingCNAMEs(false);
        }
    };

    // Propagation check
    const checkPropagation = useCallback(async (record) => {
        setPropagationStatus(prev => ({ ...prev, [record.id]: 'checking' }));
        try {
            const { data } = await recordsAPI.checkPropagation(record.name, record.type);
            setPropagationStatus(prev => ({ ...prev, [record.id]: data }));
        } catch {
            setPropagationStatus(prev => ({ ...prev, [record.id]: 'error' }));
        }
    }, []);

    const checkAllPropagation = async () => {
        for (const record of filteredRecords.slice(0, 20)) {
            await checkPropagation(record);
            await new Promise(r => setTimeout(r, 200));
        }
    };

    // Bulk operations
    const toggleSelect = (id) => {
        setSelectedRecords(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedRecords.size === filteredRecords.length) {
            setSelectedRecords(new Set());
        } else {
            setSelectedRecords(new Set(filteredRecords.map(r => r.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`Deletar ${selectedRecords.size} registros selecionados?`)) return;
        let deleted = 0;
        for (const id of selectedRecords) {
            try {
                await zonesAPI.deleteRecord(zone.id, id);
                deleted++;
            } catch {}
            await new Promise(r => setTimeout(r, 100));
        }
        setMessage({ type: 'success', text: `${deleted} registro(s) deletado(s).` });
        setSelectedRecords(new Set());
        refetchRecords();
    };

    const handleBulkEditTTL = async (ttl) => {
        let updated = 0;
        for (const id of selectedRecords) {
            const record = records.find(r => r.id === id);
            if (!record) continue;
            try {
                await zonesAPI.updateRecord(zone.id, id, { ...record, ttl });
                updated++;
            } catch {}
            await new Promise(r => setTimeout(r, 100));
        }
        setMessage({ type: 'success', text: `TTL alterado em ${updated} registro(s).` });
        setSelectedRecords(new Set());
        refetchRecords();
    };

    const handleBulkToggleProxy = async () => {
        let updated = 0;
        for (const id of selectedRecords) {
            const record = records.find(r => r.id === id);
            if (!record || !['A', 'AAAA', 'CNAME'].includes(record.type)) continue;
            try {
                await zonesAPI.updateRecord(zone.id, id, { ...record, proxied: !record.proxied });
                updated++;
            } catch {}
            await new Promise(r => setTimeout(r, 100));
        }
        setMessage({ type: 'success', text: `Proxy toggled em ${updated} registro(s).` });
        setSelectedRecords(new Set());
        refetchRecords();
    };

    // Template handler
    const handleTemplate = async (template) => {
        setShowTemplates(false);
        if (template.records.length === 1) {
            setEditingRecord(null);
            setShowModal(true);
            // Pre-fill will happen through template state
            setTimeout(() => {
                const rec = template.records[0];
                setEditingRecord(null);
                setShowModal(false);
                setTimeout(() => {
                    setEditingRecord({ ...rec, _isTemplate: true });
                    setShowModal(true);
                }, 50);
            }, 0);
        } else {
            try {
                await zonesAPI.bulkCreateRecords(zone.id, template.records);
                setMessage({ type: 'success', text: `Template "${template.name}" aplicado! ${template.records.length} registros criados.` });
                refetchRecords();
            } catch (error) {
                setMessage({ type: 'error', text: `Erro ao aplicar template: ${error.message}` });
            }
        }
    };

    const renderPropagation = (record) => {
        const status = propagationStatus[record.id];
        if (!status) return null;
        if (status === 'checking') return <span className="propagation-checking">...</span>;
        if (status === 'error') return <span className="propagation-fail">!</span>;
        return status.propagated
            ? <span className="propagation-ok" title="Propagado">P</span>
            : <span className="propagation-fail" title="Nao propagado">X</span>;
    };

    const getTypeStyle = (type) => TYPE_COLORS[type] || { bg: '#f3f4f6', color: '#374151' };

    return (
        <div>
            {/* Zone Info Header */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                    <div>
                        <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: '0.5rem' }}>
                            {'\u2190'} Voltar para Dominios
                        </button>
                        <h2 className="card-title" style={{ marginBottom: '0.25rem' }}>
                            Gerenciando DNS - {zone.name}
                        </h2>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                            <span>
                                <strong>Status:</strong>{' '}
                                <span className={`badge ${zone.status === 'active' ? 'success' : 'pending'}`}>
                                    {zone.status === 'active' ? 'Ativo' : 'Pendente'}
                                </span>
                            </span>
                            <span>
                                <strong>ID:</strong> <code style={{ fontSize: '0.75rem' }}>{zone.id.substring(0, 16)}...</code>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Records Management */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Registros DNS ({records.length})</h3>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div className="view-mode-toggle">
                            <button className={`vmt-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>Lista</button>
                            <button className={`vmt-btn ${viewMode === 'tree' ? 'active' : ''}`} onClick={() => setViewMode('tree')}>Arvore</button>
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={checkAllPropagation}>Verificar DNS</button>
                        <button className="btn btn-secondary btn-sm" onClick={handleFixCNAMEs} disabled={fixingCNAMEs}>
                            {fixingCNAMEs ? 'Corrigindo...' : 'Fix CNAMEs'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setShowTemplates(true)}>Templates</button>
                        <button className="btn btn-primary" onClick={handleCreateRecord}>+ Novo Registro</button>
                    </div>
                </div>

                {message && (
                    <div className={message.type === 'success' ? 'success-message' : 'error'}>
                        {message.text}
                        <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>{'\u2715'}</button>
                    </div>
                )}

                <div className="records-header">
                    <div className="search-box">
                        <span className="search-icon">&#128269;</span>
                        <input type="text" className="search-input" placeholder="Buscar registros..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {filteredRecords.length} de {records.length} registros
                    </div>
                </div>

                {recordsLoading ? (
                    <div className="loading"><div className="spinner"></div><span>Carregando registros...</span></div>
                ) : viewMode === 'tree' ? (
                    <SubdomainTree records={filteredRecords} zone={zone} onEditRecord={handleEditRecord} onDeleteRecord={handleDeleteRecord} />
                ) : (
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th style={{ width: '2rem' }}>
                                        <input type="checkbox" checked={selectedRecords.size === filteredRecords.length && filteredRecords.length > 0} onChange={toggleSelectAll} />
                                    </th>
                                    <th>Nome</th>
                                    <th>Tipo</th>
                                    <th>Conteudo</th>
                                    <th>TTL</th>
                                    <th>Proxy</th>
                                    <th>DNS</th>
                                    <th>Acoes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecords.length === 0 ? (
                                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhum registro encontrado</td></tr>
                                ) : (
                                    filteredRecords.map((record) => {
                                        const ts = getTypeStyle(record.type);
                                        return (
                                            <tr key={record.id} className={selectedRecords.has(record.id) ? 'row-selected' : ''}>
                                                <td><input type="checkbox" checked={selectedRecords.has(record.id)} onChange={() => toggleSelect(record.id)} /></td>
                                                <td>
                                                    <strong>{record.name}</strong>
                                                    {notes[record.id] && <span className="note-indicator" title={notes[record.id].note}>{'\uD83D\uDCDD'}</span>}
                                                </td>
                                                <td><span className="badge" style={{ background: ts.bg, color: ts.color }}>{record.type}</span></td>
                                                <td style={{ maxWidth: '350px', wordBreak: 'break-all' }}>{record.content}</td>
                                                <td>{record.ttl === 1 ? 'Auto' : record.ttl}</td>
                                                <td>
                                                    {record.proxied
                                                        ? <span className="badge success">{'\u2713'}</span>
                                                        : <span className="badge" style={{ background: '#f3f4f6', color: '#6b7280' }}>-</span>}
                                                </td>
                                                <td>{renderPropagation(record)}</td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                                                        <button className="btn btn-secondary btn-sm" onClick={() => checkPropagation(record)} title="Verificar propagacao">DNS</button>
                                                        <button className="btn btn-secondary btn-sm" onClick={() => handleEditRecord(record)}>Editar</button>
                                                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteRecord(record)}>Del</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <BulkActions
                selectedCount={selectedRecords.size}
                onBulkDelete={handleBulkDelete}
                onBulkEditTTL={handleBulkEditTTL}
                onBulkToggleProxy={handleBulkToggleProxy}
                onDeselectAll={() => setSelectedRecords(new Set())}
            />

            {showModal && (
                <RecordModal record={editingRecord} zoneName={zone.name} onSave={handleSaveRecord} onClose={() => setShowModal(false)} />
            )}

            {showTemplates && (
                <RecordTemplates zoneName={zone.name} onSelectTemplate={handleTemplate} onClose={() => setShowTemplates(false)} />
            )}
        </div>
    );
};

export default ZoneRecords;
